import React, { useEffect } from 'react';
import './Modal.css';

/**
 * Shared modal shell - overlay, centered/mobile-full-screen box, sticky
 * header with title + close button, scrollable body, optional sticky
 * footer, background-scroll lock, and Escape-to-close.
 *
 * Deliberately uses its own uniquely-prefixed classes (`tpc-modal-*`)
 * rather than the app's extremely common generic `.modal-overlay`/
 * `.modal-content` names (used ad-hoc by 80+ components with their own
 * bespoke styles) to avoid any risk of unintended style bleed - CSS in
 * this app is not module-scoped, so reusing those names here could affect
 * unrelated modals elsewhere.
 *
 * Usage:
 *   <Modal isOpen={showX} onClose={() => setShowX(false)} title="Add Component" maxWidth="700px">
 *     ...body content...
 *   </Modal>
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = '560px',
  closeOnOverlayClick = true,
  className = ''
}) => {
  // Lock background scroll while open, and let Escape close it.
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="tpc-modal-overlay"
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div
        className={`tpc-modal${className ? ` ${className}` : ''}`}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {title !== undefined && (
          <div className="tpc-modal-header">
            <h3 className="tpc-modal-title">{title}</h3>
            <button
              type="button"
              className="tpc-modal-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        )}
        <div className="tpc-modal-body">
          {children}
        </div>
        {footer && (
          <div className="tpc-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
